import { useEffect, useRef, useState } from 'react'
import { isAxiosError } from 'axios'
import { Bell, Mail, Plus, Trash2 } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Chip, ChipGroup } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'

import { relativeTime } from '@/lib/format'

import { DELIVERABLE_EVENTS, NOTIFICATION_EVENT_GROUPS } from './notificationEvents'
import {
  AUDIENCE_VARIABLES,
  SUGGESTED_EVENT_VARIABLES,
  type NotificationVariableDef,
} from './notificationVariables'
import {
  useCreateNotificationTemplate,
  useDeleteNotificationTemplate,
  useNotificationCredential,
  useNotificationOutbox,
  useNotificationTemplates,
  useSaveNotificationCredential,
  useSendTestEmail,
  useUpdateNotificationTemplate,
  type NotificationChannel,
  type NotificationTemplate,
  type NotificationTemplateUpsert,
  type TriggerType,
} from './notificationsApi'

/**
 * Tenant-manager-only admin surface for the SMS/Email notification engine.
 * Gated server-side by NOTIFICATION_CONFIG_MANAGE (granted only to
 * TENANT_ADMIN) — this page has no client-side role guard, same convention
 * as every other /admin/* page (see UsersAdminPage): a non-admin gets a
 * 403 and a friendly inline error.
 *
 * <p>Three sections: templates (both trigger types), a live outbox/audit
 * view, and provider credentials. The credentials form is write-only by
 * design — the secret input is never pre-filled from the GET response,
 * because the backend never returns it (see notificationsApi.ts's
 * CredentialMetadata — there is no field for it).</p>
 */
export function NotificationsAdminPage() {
  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div>
        <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">
          Tenant administration
        </p>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight text-navy-900">
          <Bell className="h-5 w-5" /> Notifications
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          SMS and email templates, trigger bindings (event or scheduled), the
          send outbox, and provider credentials.
        </p>
      </div>

      <TemplatesSection />
      <OutboxSection />
      <CredentialsSection />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

const RECIPIENT_AUDIENCES = ['PTP_EXPIRING_TODAY', 'PTP_BROKEN_UNRESOLVED'] as const

/**
 * The backend validates credential/template config and returns
 * `{code, message}` on a 400 — surface that message rather than a generic
 * failure, otherwise the operator can't tell which field it rejected.
 */
function saveErrorMessage(error: unknown): string {
  return apiErrorMessage(error, 'Save failed.')
}

/**
 * Same shape, different fallback — a failed test send saying "Save failed."
 * sent more than one operator looking at the wrong thing.
 */
function apiErrorMessage(error: unknown, fallback: string): string {
  if (isAxiosError(error) && error.response?.status === 403) return 'NOTIFICATION_CONFIG_MANAGE is required.'
  // The test-send endpoint is rate-limited per admin. Retry-After is the only
  // part of a 429 an operator can act on, so lead with it — same treatment as
  // the login screen's lockout message.
  if (isAxiosError(error) && error.response?.status === 429) {
    const retryAfter = Number(error.response.headers?.['retry-after'])
    return Number.isFinite(retryAfter) && retryAfter > 0
      ? `Too many test sends. Try again in ${retryAfter}s.`
      : 'Too many test sends. Try again shortly.'
  }
  if (isAxiosError(error)) return error.response?.data?.message ?? fallback
  return fallback
}

function TemplatesSection() {
  const templates = useNotificationTemplates()
  const del = useDeleteNotificationTemplate()
  const [showCreate, setShowCreate] = useState(false)
  const [editing, setEditing] = useState<NotificationTemplate | null>(null)

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Templates</CardTitle>
            <CardDescription>
              {templates.data ? `${templates.data.length} templates` : '—'}
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="h-3.5 w-3.5" />
            New template
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        {(showCreate || editing) && (
          <div className="border-b border-slate-200 px-5 py-4">
            <TemplateForm
              existing={editing}
              onClose={() => {
                setShowCreate(false)
                setEditing(null)
              }}
            />
          </div>
        )}

        {templates.isPending && <SkeletonRows rows={4} cols={6} />}
        {templates.isError && (
          <div className="px-5 pb-4 text-sm text-negative">
            Couldn't load templates — NOTIFICATION_CONFIG_MANAGE is required.
          </div>
        )}
        {templates.data && templates.data.length === 0 && (
          <div className="px-5 py-10 text-center">
            <p className="text-sm font-medium text-navy-900">No templates yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Click "New template" to bind a channel to an event or a schedule.
            </p>
          </div>
        )}
        {templates.data && templates.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-navy-50/40 text-left text-2xs uppercase tracking-wider text-navy-700">
                <tr>
                  <th className="px-5 py-2 font-medium">Channel</th>
                  <th className="px-3 py-2 font-medium">Key</th>
                  <th className="px-3 py-2 font-medium">Trigger</th>
                  <th className="px-3 py-2 font-medium">Enabled</th>
                  <th className="px-3 py-2 font-medium">Last run</th>
                  <th className="px-3 py-2 font-medium">Updated</th>
                  <th className="px-5 py-2 text-right font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {templates.data.map((t) => (
                  <tr key={`${t.channel}:${t.templateKey}`} className="border-b border-slate-200 last:border-0 hover:bg-navy-50">
                    <td className="px-5 py-2">
                      <Badge tone={t.channel === 'EMAIL' ? 'info' : 'navy'}>{t.channel}</Badge>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-navy-900">{t.templateKey}</td>
                    <td className="px-3 py-2 text-navy-900">
                      {t.triggerType === 'EVENT' ? `Event: ${t.eventName}` : `Cron: ${t.cronExpression}`}
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={t.enabled ? 'positive' : 'slate'}>{t.enabled ? 'Enabled' : 'Disabled'}</Badge>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {t.triggerType !== 'CRON'
                        ? '—'
                        : t.lastCronRunAt
                        ? relativeTime(t.lastCronRunAt)
                        : 'Never'}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{relativeTime(t.updatedAt)}</td>
                    <td className="px-5 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="outline" size="sm" onClick={() => setEditing(t)}>
                          Edit
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            if (window.confirm(`Delete template ${t.templateKey}? This stops it from firing immediately.`)) {
                              del.mutate({ channel: t.channel, templateKey: t.templateKey })
                            }
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function TemplateForm({ existing, onClose }: { existing: NotificationTemplate | null; onClose: () => void }) {
  const create = useCreateNotificationTemplate()
  const update = useUpdateNotificationTemplate()
  const mutation = existing ? update : create

  const [channel, setChannel] = useState<NotificationChannel>(existing?.channel ?? 'SMS')
  const [templateKey, setTemplateKey] = useState(existing?.templateKey ?? '')
  const [triggerType, setTriggerType] = useState<TriggerType>(existing?.triggerType ?? 'EVENT')
  const [eventName, setEventName] = useState(existing?.eventName ?? '')
  const [cronExpression, setCronExpression] = useState(existing?.cronExpression ?? '0 0 10 * * *')
  const [recipientAudience, setRecipientAudience] = useState(existing?.recipientAudience ?? RECIPIENT_AUDIENCES[0])
  const [providerTemplateId, setProviderTemplateId] = useState(existing?.providerTemplateId ?? '')
  const [subject, setSubject] = useState(existing?.subject ?? '')
  const [body, setBody] = useState(existing?.body ?? '')
  const [variables, setVariables] = useState<string[]>(
    existing ? (JSON.parse(existing.variablesJson || '[]') as string[]) : [],
  )
  const [customVariable, setCustomVariable] = useState('')
  const [enabled, setEnabled] = useState(existing?.enabled ?? true)

  function toggleVariable(name: string) {
    setVariables((cur) => (cur.includes(name) ? cur.filter((v) => v !== name) : [...cur, name]))
  }

  function addCustomVariable() {
    const name = customVariable.trim()
    if (!name || variables.includes(name)) return
    setVariables((cur) => [...cur, name])
    setCustomVariable('')
  }

  const variableDefs: NotificationVariableDef[] =
    triggerType === 'CRON' ? (AUDIENCE_VARIABLES[recipientAudience] ?? []) : SUGGESTED_EVENT_VARIABLES
  const variableSuggestions = variableDefs.map((v) => v.name)
  // Anything already selected but not in the current suggestion set (e.g. a
  // custom addition, or left over from switching audience) still needs a chip.
  const allChipNames = Array.from(new Set([...variableSuggestions, ...variables]))
  const variableDefByName = new Map(variableDefs.map((v) => [v.name, v]))

  const canSubmit = templateKey.trim().length > 0
    && (triggerType === 'EVENT' ? eventName.trim().length > 0 : cronExpression.trim().length > 0)

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    const body_: NotificationTemplateUpsert = {
      channel,
      templateKey: templateKey.trim(),
      triggerType,
      eventName: triggerType === 'EVENT' ? eventName.trim() : null,
      cronExpression: triggerType === 'CRON' ? cronExpression.trim() : null,
      recipientAudience: triggerType === 'CRON' ? recipientAudience : null,
      providerTemplateId: providerTemplateId.trim() || null,
      subject: channel === 'EMAIL' ? subject : null,
      body: channel === 'EMAIL' ? body : null,
      variables,
      enabled,
    }
    mutation.mutate(body_, { onSuccess: onClose })
  }

  const errorMsg = mutation.isError ? saveErrorMessage(mutation.error) : null

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <Label>Channel</Label>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value as NotificationChannel)}
            disabled={!!existing}
            className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-navy-900 disabled:opacity-60"
          >
            <option value="SMS">SMS</option>
            <option value="EMAIL">EMAIL</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <Label>Template key</Label>
          <Input
            value={templateKey}
            onChange={(e) => setTemplateKey(e.target.value.toUpperCase())}
            placeholder="PTP.CONFIRMATION_EMAIL"
            disabled={!!existing}
            required
          />
        </div>
      </div>

      <div>
        <Label>Trigger</Label>
        <div className="mt-1 flex gap-2">
          <Button type="button" size="sm" variant={triggerType === 'EVENT' ? 'default' : 'outline'} onClick={() => setTriggerType('EVENT')}>
            Event
          </Button>
          <Button type="button" size="sm" variant={triggerType === 'CRON' ? 'default' : 'outline'} onClick={() => setTriggerType('CRON')}>
            Scheduled (cron)
          </Button>
        </div>
      </div>

      {triggerType === 'EVENT' ? (
        <div>
          <Label>Event</Label>
          <select
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            required
            className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-navy-900"
          >
            <option value="" disabled>Choose an event…</option>
            {NOTIFICATION_EVENT_GROUPS.map((g) => {
              // Only events with a real send path are offered. The full audit
              // catalog used to be listed, so an admin could bind a template to
              // any of ~90 events, see it saved and enabled, and never learn
              // that nothing would ever be sent.
              const deliverable = g.events.filter((ev) => DELIVERABLE_EVENTS.includes(ev))
              if (deliverable.length === 0) return null
              return (
                <optgroup key={g.group} label={g.group}>
                  {deliverable.map((ev) => (
                    <option key={ev} value={ev}>{ev}</option>
                  ))}
                </optgroup>
              )
            })}
          </select>
          <p className="mt-1 text-2xs text-muted-foreground">
            The events that can notify a customer today. This template sends whenever the chosen
            event occurs, subject to do-not-contact.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Cron expression (sec min hour day month weekday)</Label>
            <Input value={cronExpression} onChange={(e) => setCronExpression(e.target.value)} required />
          </div>
          <div>
            <Label>Recipient audience</Label>
            <select
              value={recipientAudience}
              onChange={(e) => setRecipientAudience(e.target.value)}
              className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-navy-900"
            >
              {RECIPIENT_AUDIENCES.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {channel === 'SMS' ? (
        <div>
          <Label>DLT template ID</Label>
          <Input value={providerTemplateId} onChange={(e) => setProviderTemplateId(e.target.value)} placeholder="Provider-approved template id" />
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <Label>Subject (Mustache)</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Your PTP for {{loanAccountNo}}" />
          </div>
          <div>
            <Label>Body (Mustache)</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Dear {{customerName}}, ..." />
          </div>
        </div>
      )}

      <div>
        <Label>Variables</Label>
        <p className="mb-2 mt-0.5 text-2xs text-muted-foreground">
          {triggerType === 'CRON'
            ? 'What this recipient audience actually provides — click to include in the whitelist.'
            : "Common variable names to start from — this event isn't wired to a specific payload yet, so confirm the real set with engineering."}
        </p>
        <ChipGroup>
          {allChipNames.map((name) => (
            <Chip key={name} active={variables.includes(name)} onClick={() => toggleVariable(name)}>
              {name}
            </Chip>
          ))}
        </ChipGroup>
        <div className="mt-2 flex items-center gap-2">
          <Input
            value={customVariable}
            onChange={(e) => setCustomVariable(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addCustomVariable()
              }
            }}
            placeholder="Add another variable name…"
            className="h-8 max-w-xs text-sm"
          />
          <Button type="button" variant="outline" size="sm" onClick={addCustomVariable}>
            Add
          </Button>
        </div>
        {variables.length > 0 && (
          <p className="mt-2 text-2xs text-muted-foreground">
            Whitelisted: <span className="font-mono">{variables.join(', ')}</span>
          </p>
        )}

        <div className="mt-3">
          <p className="mb-1 text-2xs font-medium text-navy-700">
            Type &amp; status reference — {triggerType === 'CRON' ? recipientAudience : 'this event'}
          </p>
          <VariableReferenceTable
            rows={allChipNames.map((name) => {
              const def = variableDefByName.get(name)
              return def ? { name: def.name, type: def.type, status: def.status } : { name, status: 'custom' as const }
            })}
          />
        </div>

        <details className="mt-2 text-2xs">
          <summary className="cursor-pointer select-none text-navy-600 hover:text-navy-800">
            Show full variable catalog (all audiences &amp; events)
          </summary>
          <div className="mt-2 space-y-3">
            {RECIPIENT_AUDIENCES.map((a) => (
              <VariableReferenceTable key={a} title={a} rows={AUDIENCE_VARIABLES[a] ?? []} />
            ))}
            <VariableReferenceTable title="Event triggers (suggested)" rows={SUGGESTED_EVENT_VARIABLES} />
          </div>
        </details>
      </div>

      <label className="flex items-center gap-2 text-sm text-navy-900">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
        Enabled
      </label>

      {errorMsg && (
        <Alert variant="destructive">
          <AlertTitle>Could not save</AlertTitle>
          <AlertDescription>{errorMsg}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={!canSubmit || mutation.isPending}>
          {mutation.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create template'}
        </Button>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Variable reference table
// ---------------------------------------------------------------------------

const VARIABLE_TYPE_LABEL: Record<NotificationVariableDef['type'], string> = {
  string: 'String',
  decimal: 'Decimal',
  date: 'Date',
  guid: 'GUID',
}

// guid intentionally uses 'navy', not 'warning' — badge tones elsewhere in this
// app carry real status meaning (e.g. warning = needs attention), so a type
// label shouldn't borrow that color decoratively. The all-caps "GUID" text is
// what sets it apart from String/Decimal/Date.
const VARIABLE_TYPE_TONE: Record<NotificationVariableDef['type'], BadgeProps['tone']> = {
  string: 'slate',
  decimal: 'navy',
  date: 'info',
  guid: 'navy',
}

const VARIABLE_STATUS_LABEL: Record<string, string> = {
  live: 'Live',
  suggested: 'Suggested',
  custom: 'Custom',
}

const VARIABLE_STATUS_TONE: Record<string, BadgeProps['tone']> = {
  live: 'positive',
  suggested: 'slate',
  custom: 'warning',
}

function VariableReferenceTable({
  title,
  rows,
}: {
  title?: string
  rows: Array<{ name: string; type?: NotificationVariableDef['type']; status: string }>
}) {
  return (
    <div className="overflow-hidden rounded-md border border-slate-200">
      {title && (
        <div className="border-b border-slate-200 bg-navy-50/40 px-3 py-1.5 text-2xs font-semibold uppercase tracking-wider text-navy-700">
          {title}
        </div>
      )}
      <table className="w-full text-2xs">
        <thead className="border-b border-slate-200 text-left text-slate-500">
          <tr>
            <th className="px-3 py-1.5 font-medium">Name</th>
            <th className="px-3 py-1.5 font-medium">Type</th>
            <th className="px-3 py-1.5 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} className="border-b border-slate-100 last:border-0">
              <td className="px-3 py-1.5 font-mono text-navy-900">{r.name}</td>
              <td className="px-3 py-1.5">
                {r.type ? (
                  <Badge tone={VARIABLE_TYPE_TONE[r.type]}>{VARIABLE_TYPE_LABEL[r.type]}</Badge>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </td>
              <td className="px-3 py-1.5">
                <Badge tone={VARIABLE_STATUS_TONE[r.status] ?? 'slate'}>
                  {VARIABLE_STATUS_LABEL[r.status] ?? r.status}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Outbox
// ---------------------------------------------------------------------------

const STATUS_TONE: Record<string, 'positive' | 'warning' | 'negative' | 'slate' | 'info'> = {
  SENT: 'positive',
  PENDING: 'slate',
  SENDING: 'info',
  FAILED: 'warning',
  DEAD: 'negative',
}

function OutboxSection() {
  const [status, setStatus] = useState('')
  const [channel, setChannel] = useState('')
  const outbox = useNotificationOutbox({ status: status || undefined, channel: channel || undefined })

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>Outbox</CardTitle>
            <CardDescription>Send attempts, most recent first — refreshes every 15s.</CardDescription>
          </div>
          <div className="flex gap-2">
            <select value={channel} onChange={(e) => setChannel(e.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs text-navy-900">
              <option value="">All channels</option>
              <option value="SMS">SMS</option>
              <option value="EMAIL">EMAIL</option>
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs text-navy-900">
              <option value="">All statuses</option>
              <option value="PENDING">Pending</option>
              <option value="SENDING">Sending</option>
              <option value="SENT">Sent</option>
              <option value="FAILED">Failed</option>
              <option value="DEAD">Dead</option>
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        {outbox.isPending && <SkeletonRows rows={4} cols={6} />}
        {outbox.isError && (
          <div className="px-5 pb-4 text-sm text-negative">Couldn't load outbox.</div>
        )}
        {outbox.data && outbox.data.length === 0 && (
          <div className="px-5 py-10 text-center text-sm text-muted-foreground">Nothing here.</div>
        )}
        {outbox.data && outbox.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-navy-50/40 text-left text-2xs uppercase tracking-wider text-navy-700">
                <tr>
                  <th className="px-5 py-2 font-medium">Channel</th>
                  <th className="px-3 py-2 font-medium">Template</th>
                  <th className="px-3 py-2 font-medium">Recipient</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Attempts</th>
                  <th className="px-3 py-2 font-medium">Created</th>
                  <th className="px-5 py-2 font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {outbox.data.map((o) => (
                  <tr key={o.outboxId} className="border-b border-slate-200 last:border-0 hover:bg-navy-50">
                    <td className="px-5 py-2"><Badge tone={o.channel === 'EMAIL' ? 'info' : 'navy'}>{o.channel}</Badge></td>
                    <td className="px-3 py-2 font-mono text-xs text-navy-900">{o.templateKey}</td>
                    <td className="px-3 py-2 text-navy-900">{o.recipient}</td>
                    <td className="px-3 py-2"><Badge tone={STATUS_TONE[o.status] ?? 'slate'}>{o.status}</Badge></td>
                    <td className="px-3 py-2 text-navy-900">{o.attempts}</td>
                    <td className="px-3 py-2 text-muted-foreground">{relativeTime(o.createdAt)}</td>
                    <td className="px-5 py-2 text-2xs text-negative">{o.errorMessage ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Credentials
// ---------------------------------------------------------------------------

function CredentialsSection() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Mail className="h-4 w-4" /> Provider credentials</CardTitle>
        <CardDescription>
          Once saved, the secret is never shown again — only whether it's configured.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6 sm:grid-cols-2">
        <SmtpCredentialForm />
        <Msg91CredentialForm />
      </CardContent>
    </Card>
  )
}

function SmtpCredentialForm() {
  const cred = useNotificationCredential('SMTP')
  const save = useSaveNotificationCredential('SMTP')
  const testSend = useSendTestEmail()
  const [host, setHost] = useState('')
  const [port, setPort] = useState('587')
  const [username, setUsername] = useState('')
  const [from, setFrom] = useState('')
  const [starttls, setStarttls] = useState(true)
  const [secret, setSecret] = useState('')
  const [testDialogOpen, setTestDialogOpen] = useState(false)
  // The address the last send actually went to, so the success alert can name
  // it. Null means "the caller's own record" — the client never learns that
  // address, the backend resolves it.
  const [sentTo, setSentTo] = useState<string | null>(null)

  // Populate non-secret fields once, from the metadata GET — the secret
  // field is intentionally never touched here (the backend never returns it).
  // Guarded by a ref because react-query hands back a fresh `data` identity on
  // every refetch; without it a background refetch mid-edit would silently
  // revert whatever the operator has typed back to the stored values.
  const hydrated = useRef(false)
  useEffect(() => {
    if (!cred.data || hydrated.current) return
    hydrated.current = true
    setHost((cred.data.config.host as string) ?? '')
    setPort(String(cred.data.config.port ?? '587'))
    setUsername((cred.data.config.username as string) ?? '')
    setFrom((cred.data.config.from as string) ?? '')
    setStarttls(cred.data.config.starttls !== false)
  }, [cred.data])

  // Any edit or save invalidates the last test result — leaving a stale
  // "sent" (or "refused") next to changed settings reads as if it applied to
  // them. Wrap every field setter rather than relying on the operator noticing.
  function edited<T>(setter: (v: T) => void) {
    return (v: T) => {
      testSend.reset()
      setter(v)
    }
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    testSend.reset()
    save.mutate({
      config: { host, port: Number(port), username, from, starttls },
      ...(secret ? { secret } : {}),
    }, { onSuccess: () => setSecret('') })
  }

  return (
    <>
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-navy-900">SMTP (Email)</p>
        {cred.data?.configured && <Badge tone="positive">Configured</Badge>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Host</Label>
          <Input value={host} onChange={(e) => edited(setHost)(e.target.value)} placeholder="smtp.example.com" required />
        </div>
        <div>
          <Label>Port</Label>
          <Input value={port} onChange={(e) => edited(setPort)(e.target.value)} placeholder="587" required />
        </div>
        <div>
          <Label>Username</Label>
          <Input value={username} onChange={(e) => edited(setUsername)(e.target.value)} required />
        </div>
        <div>
          <Label>From address</Label>
          <Input type="email" value={from} onChange={(e) => edited(setFrom)(e.target.value)} required />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-navy-900">
        <input type="checkbox" checked={starttls} onChange={(e) => edited(setStarttls)(e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
        STARTTLS
      </label>
      <div>
        <Label>Password {cred.data?.hasSecret && <span className="text-2xs font-normal text-muted-foreground">(already set — leave blank to keep it)</span>}</Label>
        {/* Required only on first setup: with no stored secret, saving without
            one produces a credential that can never send. Once one exists,
            blank means "keep it" and the field is legitimately optional. */}
        <Input type="password" value={secret} onChange={(e) => edited(setSecret)(e.target.value)} placeholder={cred.data?.hasSecret ? '••••••••' : ''} autoComplete="new-password" required={!cred.data?.hasSecret} />
      </div>
      {save.isError && <p className="text-2xs text-negative">{saveErrorMessage(save.error)}</p>}
      {save.isSuccess && <p className="text-2xs text-positive">Saved.</p>}

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save SMTP settings'}
        </Button>
        {/* Pointless before a secret exists — the send would only ever come
            back smtp_not_configured. */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!cred.data?.hasSecret || testSend.isPending}
          onClick={() => {
            testSend.reset()
            setTestDialogOpen(true)
          }}
        >
          {testSend.isPending ? 'Sending…' : 'Send test email…'}
        </Button>
      </div>

      {/* Below the button, and an Alert rather than 10px of grey text: the
          previous inline line sat above the form's own Save messages and was
          routinely missed, which made a failing SMTP config look like a button
          that did nothing. A refusal arrives as 200 + accepted:false, so
          isError here means the request itself failed — the two read
          differently on purpose. */}
      {testSend.isError && (
        <Alert variant="destructive">
          <AlertTitle>Test email failed</AlertTitle>
          <AlertDescription>{apiErrorMessage(testSend.error, 'Test send failed.')}</AlertDescription>
        </Alert>
      )}
      {testSend.isSuccess && (
        testSend.data.accepted
          ? (
            <Alert variant="positive">
              <AlertTitle>Test email sent</AlertTitle>
              <AlertDescription>
                {sentTo
                  ? `Sent to ${sentTo} — check that inbox, including spam.`
                  : 'Sent to the email address on your own user record — check your inbox, including spam.'}
              </AlertDescription>
            </Alert>
          )
          : (
            <Alert variant="destructive">
              <AlertTitle>Test email refused by the provider</AlertTitle>
              <AlertDescription>{testSend.data.detail ?? 'unknown error'}</AlertDescription>
            </Alert>
          )
      )}
    </form>

    {/* Rendered as a sibling of the form, not inside it: a second
        input[type="email"] within the SMTP form's subtree would collide with
        the From field for anything selecting on that (the e2e page object
        does), and a nested <form> is invalid HTML anyway. */}
    {testDialogOpen && (
      <TestEmailDialog
        pending={testSend.isPending}
        onClose={() => setTestDialogOpen(false)}
        onSend={(recipient) => {
          setSentTo(recipient || null)
          testSend.mutate(recipient, { onSuccess: () => setTestDialogOpen(false) })
        }}
      />
    )}
    </>
  )
}

/**
 * Asks for the recipient before sending. Blank is legitimate and means "my own
 * address" — the endpoint's original behaviour, and the only option for an
 * admin who wants to check their own record works.
 *
 * The dialog stays open on failure so the error is readable next to the address
 * that caused it; the parent closes it on success.
 */
function TestEmailDialog({
  pending,
  onClose,
  onSend,
}: {
  pending: boolean
  onClose: () => void
  onSend: (recipient: string) => void
}) {
  const [recipient, setRecipient] = useState('')

  // ModalShell in ErasurePage closes on backdrop click only; Escape is what
  // people actually press.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Send test email"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-navy-900/50 backdrop-blur-sm"
      />
      <div className="relative z-10 w-full max-w-md rounded-md border border-slate-200 bg-white shadow-lift">
        <div className="flex h-12 items-center border-b border-slate-200 px-5">
          <span className="text-2xs font-semibold uppercase tracking-wider text-navy-700">
            Send test email
          </span>
        </div>
        <form
          className="space-y-3 p-5"
          onSubmit={(e) => {
            e.preventDefault()
            onSend(recipient.trim())
          }}
        >
          <div>
            <Label>Recipient</Label>
            {/* Not required: empty falls back to the caller's own address
                server-side. type="email" gives us native format validation for
                free — the repo has no validation helper. */}
            <Input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="you@example.com"
              autoFocus
            />
            <p className="mt-1 text-2xs text-muted-foreground">
              Leave blank to send to the address on your own user record.
            </p>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={pending}>
              {pending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Msg91CredentialForm() {
  const cred = useNotificationCredential('MSG91')
  const save = useSaveNotificationCredential('MSG91')
  const [senderId, setSenderId] = useState('')
  const [authKey, setAuthKey] = useState('')

  // Hydrate once — see the ref-guard note on SmtpCredentialForm.
  const hydrated = useRef(false)
  useEffect(() => {
    if (!cred.data || hydrated.current) return
    hydrated.current = true
    setSenderId((cred.data.config.sender_id as string) ?? '')
  }, [cred.data])

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    save.mutate({
      config: { sender_id: senderId },
      ...(authKey ? { secret: authKey } : {}),
    }, { onSuccess: () => setAuthKey('') })
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-navy-900">Msg91 (SMS) — tenant override</p>
        {cred.data?.configured && <Badge tone="positive">Configured</Badge>}
      </div>
      <p className="text-2xs text-muted-foreground">
        Optional — leave unset to use the platform default Msg91 account.
      </p>
      <div>
        <Label>Sender ID</Label>
        {/* The override as a whole is optional, but saving one with a blank
            sender ID stores a row that can never be used — the backend rejects
            it, so block it here rather than round-tripping a 400. */}
        <Input value={senderId} onChange={(e) => setSenderId(e.target.value.toUpperCase())} placeholder="GRVCOL" required />
      </div>
      <div>
        <Label>Auth key {cred.data?.hasSecret && <span className="text-2xs font-normal text-muted-foreground">(already set — leave blank to keep it)</span>}</Label>
        <Input type="password" value={authKey} onChange={(e) => setAuthKey(e.target.value)} placeholder={cred.data?.hasSecret ? '••••••••' : ''} autoComplete="new-password" required={!cred.data?.hasSecret} />
      </div>
      {save.isError && <p className="text-2xs text-negative">{saveErrorMessage(save.error)}</p>}
      {save.isSuccess && <p className="text-2xs text-positive">Saved.</p>}
      <Button type="submit" size="sm" disabled={save.isPending}>
        {save.isPending ? 'Saving…' : 'Save Msg91 settings'}
      </Button>
    </form>
  )
}
